import type { OptionsType, TmpCookiesObj } from 'cookies-next/lib/types'
import { setCookie, getCookies, getCookie, deleteCookie } from 'cookies-next'
import type { cookies } from 'next/headers'
import settings, { APP_ENV } from 'shared/common/settings'
import { window } from 'shared/utils/browser'
import { isVercelPreviewHost } from 'shared/utils/auth/v2/preview-host'
import { isNumber } from 'shared/utils/type-guards'

type AwaitedCookieStore = Awaited<ReturnType<typeof cookies>>

function isAwaitedCookieStore(value: unknown): value is AwaitedCookieStore {
  return (
    !!value &&
    typeof value === 'object' &&
    'getAll' in value &&
    'set' in value &&
    typeof (value as AwaitedCookieStore).getAll === 'function' &&
    typeof (value as AwaitedCookieStore).set === 'function'
  )
}

function getCookiesFromStore(cookieStore: AwaitedCookieStore): TmpCookiesObj {
  const allCookies: TmpCookiesObj = {}
  cookieStore.getAll().forEach(({ name, value }) => {
    allCookies[name] = value
  })
  return allCookies
}

function toCookiesNextOptions(ctx: Context): OptionsType | undefined {
  if (!ctx) return undefined
  const { cookies: _cookies, ...rest } = ctx
  return rest as OptionsType
}

export type Context =
  | (Omit<OptionsType, 'cookies'> & {
      cookies?: AwaitedCookieStore
      expires?: Date | string
      maxAge?: number
    })
  | undefined

export class CookieStorage implements Storage {
  length: number;
  [name: string]: any

  private ctx: Context
  private allCookies: TmpCookiesObj
  private defaultMaxAge = 2592000 // default to 30 days
  private appEnv: APP_ENV

  private cookieDomain: Record<APP_ENV, string> = {
    local: 'localhost',
    development: 'dev.guidecx.io',
    preview: 'vercel.guidecx.io',
    staging: 'staging.guidecx.io',
    production: 'guidecx.com',
  }

  constructor(ctx?: Context, appEnv: APP_ENV = settings.appEnv) {
    this.length = 0
    this.ctx = ctx
    this.appEnv = appEnv

    if (ctx?.cookies && isAwaitedCookieStore(ctx.cookies)) {
      this.allCookies = getCookiesFromStore(ctx.cookies)
    } else {
      this.allCookies = getCookies({ ...toCookiesNextOptions(ctx) })
    }

    for (const property in this.allCookies) {
      this[property] = this.allCookies[property]
      this.length = this.length + 1
    }
  }

  private getCookieStore(): AwaitedCookieStore | undefined {
    if (this.ctx?.cookies && isAwaitedCookieStore(this.ctx.cookies)) {
      return this.ctx.cookies
    }
    return undefined
  }

  private getCookieOptions() {
    return {
      path: '/',
      sameSite: 'none' as const,
      secure: true,
      domain: this.getDomain(),
    }
  }

  /**
   * Removes all key/value pairs, if there are any.
   *
   * Dispatches a storage event on Window objects holding an equivalent Storage object.
   */
  clear(): void {
    const cookieStore = this.getCookieStore()

    for (const property in this.allCookies) {
      if (cookieStore) {
        cookieStore.set(property, '', {
          ...this.getCookieOptions(),
          maxAge: 0,
        })
      } else {
        deleteCookie(property, { ...toCookiesNextOptions(this.ctx) })
      }
      delete this[property]
    }
    this.length = 0
  }

  /** Returns the current value associated with the given key, or null if the given key does not exist. */
  getItem(key: string): string | null {
    const cookieStore = this.getCookieStore()

    if (cookieStore) {
      return cookieStore.get(key)?.value ?? null
    }

    const cookieValue = getCookie(key, {
      ...toCookiesNextOptions(this.ctx),
    })?.toString()

    return cookieValue || null
  }

  /** Returns the name of the nth key, or null if n is greater than or equal to the number of key/value pairs. */
  key(index: number): string | null {
    const propertyNames = Object.keys(this.allCookies)

    return propertyNames[index] || null
  }

  /**
   * Removes the key/value pair with the given key, if a key/value pair with the given key exists.
   *
   * Dispatches a storage event on Window objects holding an equivalent Storage object.
   */
  removeItem(key: string): void {
    const cookieStore = this.getCookieStore()

    if (cookieStore) {
      cookieStore.set(key, '', {
        ...this.getCookieOptions(),
        maxAge: 0,
      })
    } else {
      deleteCookie(key, {
        path: '/',
        domain: this.getDomain(),
        ...toCookiesNextOptions(this.ctx),
      })
    }
    delete this[key]
    this.length = this.length > 0 ? this.length - 1 : 0
  }

  /**
   * @private
   * Resolves the cookie `domain` attribute for the current request.
   *
   * On Vercel preview hosts the app runs on a single dynamic host
   * (e.g. `main-git-<branch>.vercel.guidecx.io`) that the hardcoded per-env
   * domains don't match, so a domain-scoped cookie would be rejected by the
   * browser. In that case we return undefined to set a host-only cookie.
   */
  private getDomain(): string | undefined {
    const reqHeaders = this.ctx?.req?.headers
    const headerHost =
      reqHeaders instanceof Headers ? reqHeaders.get('host') : reqHeaders?.host
    const currentHost = headerHost ?? window?.location?.hostname

    if (isVercelPreviewHost(currentHost)) {
      return undefined
    }

    return this.cookieDomain[this.appEnv]
  }

  /**
   * @private
   * returns the maxAge from the provided context or a default value
   */
  private getMaxAge() {
    if (this.ctx && 'maxAge' in this.ctx && isNumber(this.ctx.maxAge)) {
      return this.ctx.maxAge
    }
    return this.defaultMaxAge
  }

  /**
   * @private
   * returns the expires property from context if provided
   */
  private getExpires(): Date | undefined {
    if (this.ctx && 'expires' in this.ctx) {
      const expires = this.ctx.expires
      if (expires instanceof Date) {
        return expires
      }
      if (typeof expires === 'string') {
        return new Date(expires)
      }
    }
    return undefined
  }

  /**
   * Sets the value of the pair identified by key to value, creating a new key/value pair if none existed for key previously.
   *
   * Throws a "QuotaExceededError" DOMException exception if the new value couldn't be set. (Setting could fail if, e.g., the user has disabled storage for the site, or if the quota has been exceeded.)
   *
   * Dispatches a storage event on Window objects holding an equivalent Storage object.
   */
  setItem(key: string, value: string): void | null {
    const expires = this.getExpires()
    const cookieStore = this.getCookieStore()

    if (cookieStore) {
      cookieStore.set(key, value, {
        ...this.getCookieOptions(),
        expires,
        maxAge: !expires ? this.getMaxAge() : undefined,
      })
    } else {
      setCookie(key, value, {
        ...toCookiesNextOptions(this.ctx),
        path: '/',
        sameSite: 'none',
        secure: true,
        domain: this.getDomain(),
        expires,
        maxAge: !expires ? this.getMaxAge() : undefined, // We don't want to set maxAge if expires is also set, since maxAge will always take precedence
      } as OptionsType)
    }
    this[key] = value
    this.length = this.length + 1
  }
}
